import React, { useState, useEffect, useCallback } from 'react';
import { FaSms, FaEnvelope, FaSyncAlt, FaChevronDown, FaSearch } from 'react-icons/fa';
import ConfirmationModal from '../ConfirmationModal';
import { getRecipients, sendMessage } from '../../services/messagingService';
import { SMS_MAX_CHARS, SMS_RATE_AUD, smsSegments } from '../../utils/smsText';

// Compose and send one SMS/email to selected franchisees, or broadcast to all active ones
// (tickets 4 and 17). Extracted from MessagingPage so that page is only the tab shell.
// SMS encoding and cost maths live in utils/smsText so this and the conversation composer agree.

const fullName = (f) => [f.firstName, f.lastName].filter(Boolean).join(' ').trim();

// Prefer the operator's full name (first + last); fall back to the franchise/business name, then code/id.
const displayName = (f) => fullName(f) || f.franchiseName || f.fseCode || f.id;

// Per-recipient result badge colours for the delivery-details panel.
const STATUS_BADGE = {
  sent: 'bg-green-100 text-green-800',
  skipped: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-800',
};

const ComposeTab = () => {
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null); // { type: 'success' | 'error', text }

  const [channel, setChannel] = useState('sms'); // 'sms' | 'email' — SMS is the prioritised default
  const [broadcast, setBroadcast] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState(''); // filter the (potentially 200+) franchisee list
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [overrideSmsLimit, setOverrideSmsLimit] = useState(false);
  const [includeName, setIncludeName] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState([]); // per-recipient outcome of the last send
  const [showResults, setShowResults] = useState(false);


  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { franchisees: list } = await getRecipients();
      setFranchisees(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message || 'Failed to load franchisees.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  // Whether a franchisee has the contact needed for the active channel.
  const hasContact = useCallback(
    (f) => (channel === 'sms' ? !!f.phoneNumber : !!f.email),
    [channel]
  );

  // Recipients we'll actually attempt: broadcast → active franchisees; otherwise the selected ones.
  const targeted = broadcast
    ? franchisees.filter((f) => f.isActive)
    : franchisees.filter((f) => selectedIds.has(f.id));
  const withContact = targeted.filter(hasContact);
  const missingContact = targeted.length - withContact.length;

  const selectable = franchisees.filter(hasContact);

  // Narrow the list (200+ franchisees): free-text search over name/code/contact, plus a "selected only" view.
  const query = search.trim().toLowerCase();
  const matchesQuery = (f) =>
    !query ||
    [f.firstName, f.lastName, fullName(f), f.franchiseName, f.fseCode, f.phoneNumber, f.email]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(query));
  const visibleRows = franchisees.filter(
    (f) => (!showSelectedOnly || selectedIds.has(f.id)) && matchesQuery(f)
  );
  const visibleSelectable = visibleRows.filter(hasContact);
  const allVisibleSelected =
    visibleSelectable.length > 0 && visibleSelectable.every((f) => selectedIds.has(f.id));
  const filterActive = !!query || showSelectedOnly;

  // Use the longest firstName for the preview so the character count reflects the worst case.
  const longestNameFranchisee = withContact.reduce((best, f) =>
    (f.firstName || '').length > (best?.firstName || '').length ? f : best,
    null
  );
  const greeting = includeName && longestNameFranchisee?.firstName ? `Hi ${longestNameFranchisee.firstName}, ` : '';
  const trimmed = message.trim();
  const effectiveMessage = greeting + trimmed;
  const segments = smsSegments(effectiveMessage);
  // Over the cap only matters when the override is off (maxLength blocks new keystrokes but won't truncate an
  // existing value left behind when the override is toggled back off) — guard so we don't POST a doomed request.
  const overLimit = channel === 'sms' && !overrideSmsLimit && effectiveMessage.length > SMS_MAX_CHARS;
  const bodyMaxLength = channel === 'sms' && !overrideSmsLimit
    ? SMS_MAX_CHARS - (includeName ? greeting.length : 0) : undefined;
  const canSend =
    !!trimmed &&
    (channel !== 'email' || !!subject.trim()) &&
    withContact.length > 0 &&
    !overLimit &&
    !sending;

  const recipientNoun = withContact.length === 1 ? 'franchisee' : 'franchisees';
  const estAud = withContact.length * segments * SMS_RATE_AUD;

  // Why Send is disabled, surfaced next to the button so the block is never silent.
  const sendHint = !trimmed
    ? 'Write a message to send.'
    : channel === 'email' && !subject.trim()
    ? 'Add a subject to send.'
    : overLimit
    ? `Message is over ${SMS_MAX_CHARS} characters — shorten it or turn on the override.`
    : withContact.length === 0
    ? broadcast
      ? `No active franchisees have ${channel === 'sms' ? 'a phone number' : 'an email'} to message.`
      : 'Select at least one franchisee.'
    : '';

  // Switching channel resets the SMS override and prunes any selection lacking the new channel's contact.
  const changeChannel = (next) => {
    if (next === channel) return;
    setChannel(next);
    setOverrideSmsLimit(false);
    setSelectedIds((prev) => {
      const kept = new Set();
      franchisees.forEach((f) => {
        const ok = next === 'sms' ? !!f.phoneNumber : !!f.email;
        if (ok && prev.has(f.id)) kept.add(f.id);
      });
      return kept;
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Select-all acts on the rows currently in view (respecting search / selected-only), so a search like
  // "VIC" lets you select just that subset. Toggling off removes only the visible ones from the selection.
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (visibleSelectable.every((f) => next.has(f.id))) {
        visibleSelectable.forEach((f) => next.delete(f.id));
      } else {
        visibleSelectable.forEach((f) => next.add(f.id));
      }
      return next;
    });
  };

  const requestSend = () => {
    if (!canSend) return;
    let summary;
    if (channel === 'sms') {
      summary = `Send this SMS to ${withContact.length} ${recipientNoun}? ~${segments} segment${segments === 1 ? '' : 's'} each — estimated ~$${estAud.toFixed(2)} AUD total.`;
    } else {
      summary = `Send this email to ${withContact.length} ${recipientNoun}?`;
    }
    if (missingContact > 0) {
      summary += ` ${missingContact} ${missingContact === 1 ? 'franchisee has' : 'franchisees have'} no ${channel === 'sms' ? 'phone number' : 'email'} and will be skipped.`;
    }
    summary += ' This cannot be undone.';
    setConfirmSummary(summary);
    setConfirmOpen(true);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const tally = await sendMessage({
        channel,
        broadcast,
        // Send the explicit ids the admin saw/approved (even for broadcast) so the server messages exactly that
        // set — not a fresh server-side scan that could bill beyond the confirmed estimate.
        franchiseeIds: broadcast ? targeted.map((f) => f.id) : [...selectedIds],
        subject: channel === 'email' ? subject : undefined,
        body: message,
        overrideSmsLimit,
        includeName,
      });
      const { sent = 0, attempted = 0, failed = 0, skipped = 0, results = [] } = tally || {};
      const skippedNote = skipped ? `, ${skipped} skipped` : '';
      setSendResults(results);
      setShowResults(skipped > 0 || failed > 0); // auto-expand details when something didn't go through
      if (sent === 0) {
        // Nothing actually went out (all skipped and/or failed). Surface it as an error and keep the form intact.
        const reasons = [...new Set(results.map((r) => r.reason).filter(Boolean))];
        setBanner({
          type: 'error',
          text: `No messages sent — ${attempted} attempted${skippedNote}${failed ? `, ${failed} failed` : ''}.${reasons.length ? ` (${reasons.join('; ')})` : ''}`,
        });
      } else if (failed > 0) {
        const reasons = [...new Set(results.filter((r) => r.status === 'failed').map((r) => r.reason).filter(Boolean))];
        setBanner({
          type: 'error',
          text: `Sent ${sent} of ${attempted}. ${failed} failed${skippedNote}.${reasons.length ? ` (${reasons.join('; ')})` : ''}`,
        });
      } else {
        setBanner({
          type: 'success',
          text: `Sent ${sent} of ${attempted} ${channel === 'sms' ? 'SMS' : 'email'} message(s)${skippedNote}.`,
        });
        // Clear selection/message only on a fully clean send so the form is ready for the next message.
        setSelectedIds(new Set());
        setMessage('');
        setSubject('');
        setIncludeName(false);
      }
    } catch (e) {
      setSendResults([]); // request threw before any per-recipient result came back
      setBanner({ type: 'error', text: e.message || 'Failed to send message.' });
    } finally {
      setSending(false);
      setConfirmOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
        <p className="text-gray-600 mb-5">
          Send an SMS or email to selected franchisees, or broadcast to all active franchisees. You'll confirm before anything sends.
        </p>

        {banner && (
          <div
            className={`mb-4 px-4 py-3 rounded border ${
              banner.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <div className="flex justify-between items-start">
              <span>{banner.text}</span>
              <button onClick={() => setBanner(null)} className="ml-4 text-sm opacity-70 hover:opacity-100">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Per-recipient outcome of the last send (who was sent / skipped / failed and why). */}
        {sendResults.length > 0 && (
          <div className="mb-4 bg-white rounded-lg shadow border border-gray-200">
            <button
              onClick={() => setShowResults((v) => !v)}
              aria-expanded={showResults}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-jims-cyan rounded-lg"
            >
              <span>
                Delivery details — {sendResults.filter((r) => r.status === 'sent').length} sent
                {' · '}{sendResults.filter((r) => r.status === 'skipped').length} skipped
                {' · '}{sendResults.filter((r) => r.status === 'failed').length} failed
              </span>
              <FaChevronDown className={`text-gray-400 transition-transform ${showResults ? 'rotate-180' : ''}`} />
            </button>
            {showResults && (
              <div className="max-h-72 overflow-y-auto border-t border-gray-100">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-2">Franchisee</th>
                      <th className="px-4 py-2">To</th>
                      <th className="px-4 py-2">Status</th>
                      <th className="px-4 py-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sendResults.map((r, i) => (
                      <tr key={`${r.franchiseeId}-${i}`} className="border-b border-gray-100">
                        <td className="px-4 py-2 text-gray-900">{r.name}</td>
                        <td className="px-4 py-2 text-gray-600 break-all">{r.to || '—'}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[r.status] || 'bg-gray-100 text-gray-600'}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-500">{r.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {error && <div className="mb-4 px-4 py-3 rounded border bg-red-50 border-red-200 text-red-800">{error}</div>}

        {/* Channel: the mode switch that governs the rest of the form. */}
        <div
          className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 mb-4"
          role="group"
          aria-label="Message channel"
        >
          {[
            { key: 'sms', label: 'SMS', Icon: FaSms },
            { key: 'email', label: 'Email', Icon: FaEnvelope },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              aria-pressed={channel === key}
              onClick={() => changeChannel(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan ${
                channel === key ? 'bg-jims-cyan text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon /> {label}
            </button>
          ))}
        </div>

        {/* Recipients */}
        <div className="bg-white rounded-lg shadow p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Recipients</h2>
            <button
              onClick={fetchRecipients}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan disabled:opacity-50 flex items-center gap-2"
            >
              <FaSyncAlt className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>

          <label className="flex items-center gap-2 mb-4 text-sm text-gray-800">
            <input type="checkbox" checked={broadcast} onChange={(e) => setBroadcast(e.target.checked)} />
            <span className="font-medium">Broadcast to all active franchisees</span>
            {broadcast && (
              <span className="text-gray-500">
                ({withContact.length} with {channel === 'sms' ? 'a phone number' : 'an email'}
                {missingContact > 0 ? `, ${missingContact} without` : ''})
              </span>
            )}
          </label>

          {!broadcast && (
            <>
              {/* Controls for a long (200+) list: free-text search + a "selected only" view. */}
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="relative flex-1 min-w-[12rem]">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, code, phone or email…"
                    aria-label="Search franchisees"
                    className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showSelectedOnly}
                    onChange={(e) => setShowSelectedOnly(e.target.checked)}
                    disabled={selectedIds.size === 0}
                  />
                  Selected only ({selectedIds.size})
                </label>
                {selectedIds.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan rounded"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Bounded, scrollable list with a sticky header so Message + Send stay reachable at 200 rows. */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full leading-normal">
                    <thead className="sticky top-0 z-10 bg-gray-100">
                      <tr className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <th className="px-4 py-3 w-8 bg-gray-100">
                          <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={toggleSelectAllVisible}
                            disabled={visibleSelectable.length === 0}
                            aria-label="Select all franchisees in view"
                          />
                        </th>
                        <th className="px-4 py-3 bg-gray-100">Franchisee</th>
                        <th className="px-4 py-3 bg-gray-100">FSE Code</th>
                        <th className="px-4 py-3 bg-gray-100">{channel === 'sms' ? 'Phone' : 'Email'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
                      ) : franchisees.length === 0 ? (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No franchisees found.</td></tr>
                      ) : visibleRows.length === 0 ? (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No franchisees match your filter.</td></tr>
                      ) : (
                        visibleRows.map((f) => {
                          const contactable = hasContact(f);
                          return (
                            <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(f.id)}
                                  onChange={() => toggleSelect(f.id)}
                                  disabled={!contactable}
                                  aria-label={`Select ${displayName(f)}`}
                                  title={contactable ? '' : `No ${channel === 'sms' ? 'phone number' : 'email'} on file`}
                                />
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="font-medium text-gray-900">{displayName(f)}</div>
                                {f.franchiseName && f.franchiseName !== displayName(f) && (
                                  <div className="text-xs text-gray-500">{f.franchiseName}</div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{f.fseCode || '—'}</td>
                              <td className={`px-4 py-3 text-sm ${contactable ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                                {(channel === 'sms' ? f.phoneNumber : f.email) || `No ${channel === 'sms' ? 'phone' : 'email'}`}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-2">
                {selectedIds.size} of {selectable.length} selected
                {filterActive && !loading ? ` · showing ${visibleRows.length} of ${franchisees.length}` : ''}
              </p>
            </>
          )}
        </div>

        {/* Message */}
        <div className="bg-white rounded-lg shadow p-5 mb-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Message</h2>

          {channel === 'email' && (
            <div className="mb-3">
              <label htmlFor="message-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                id="message-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full border border-gray-300 rounded-md py-2 px-4 focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan"
                placeholder="Email subject"
              />
            </div>
          )}

          <label htmlFor="message-body" className="block text-sm font-medium text-gray-700 mb-1">
            {channel === 'sms' ? 'SMS text' : 'Email body'}
          </label>
          <textarea
            id="message-body"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={channel === 'sms' ? 4 : 8}
            maxLength={bodyMaxLength}
            className="w-full text-sm border border-gray-300 rounded p-3 focus:ring-jims-cyan focus:border-jims-cyan"
            placeholder={channel === 'sms' ? 'Type your SMS…' : 'Type your email…'}
          />

          <label className="flex items-center gap-2 text-sm text-gray-700 mt-2">
            <input
              type="checkbox"
              checked={includeName}
              onChange={(e) => setIncludeName(e.target.checked)}
              disabled={!longestNameFranchisee?.firstName}
            />
            Include franchisee name in {channel === 'sms' ? 'SMS' : 'email'}
          </label>

          {includeName && longestNameFranchisee?.firstName && (
            <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
              <span className="font-medium text-gray-500">Preview:</span>{' '}
              <span>Hi {longestNameFranchisee.firstName}, {message || <span className="italic text-gray-400">your message will appear here</span>}</span>
              {channel === 'sms' && overLimit && !overrideSmsLimit && (
                <p className="mt-1 text-red-600 text-xs">
                  With the longest name ({longestNameFranchisee.firstName}) this message exceeds {SMS_MAX_CHARS} characters — shorten it or enable the override.
                </p>
              )}
            </div>
          )}

          {channel === 'sms' && (
            <>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={overrideSmsLimit}
                      onChange={(e) => setOverrideSmsLimit(e.target.checked)}
                    />
                    Override 160-character limit
                  </label>
                </div>
                <span className={`text-sm ${overLimit ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {effectiveMessage.length}{overrideSmsLimit ? '' : `/${SMS_MAX_CHARS}`} chars
                  {' · '}
                  {segments} segment{segments === 1 ? '' : 's'}
                  {includeName && longestNameFranchisee?.firstName && ' · includes name'}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Send: scope + cost (or the reason it's blocked) sits beside the action, so the modal is a final confirm. */}
        <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-4">
          <p className="text-sm text-gray-500">
            {canSend
              ? channel === 'sms'
                ? `${withContact.length} ${recipientNoun} · ${segments} segment${segments === 1 ? '' : 's'} each · ~$${estAud.toFixed(2)} AUD est.`
                : `${withContact.length} ${recipientNoun}`
              : sendHint}
          </p>
          <button
            onClick={requestSend}
            disabled={!canSend}
            className="shrink-0 px-6 py-2.5 text-sm font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send {channel === 'sms' ? 'SMS' : 'email'} ({withContact.length})
          </button>
        </div>
      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmSend}
        title={`Send ${channel === 'sms' ? 'SMS' : 'email'}?`}
        message={confirmSummary}
        isLoading={sending}
        loadingText="Sending…"
        confirmText="Send"
        variant="primary"
      />
    </>
  );
};

export default ComposeTab;
